import { Autocomplete, Switch, TextField, FormHelperText, FormGroup, FormControlLabel, Divider } from '@mui/material'
import {
  IntegrationConfig,
  IntegrationConfigSettingsFollowUpBossSyncContacts,
  SyncDirectionEnum,
  GroupByEnum,
} from '@sendhub/integrations/api-client-typescript-axios'
import { getEnumValues } from '../../../../utils/get-enum-values'
import * as yup from 'yup'

import { useEffect, useState } from 'react'
import { updateIntegrationConfig } from '../../../store/integration-config/integration-configs.slice'
import { useAppDispatch } from '../../../store/root'
import { FormikProvider, useFormik } from 'formik'
import SelectAccount from '../../select-account'
import { FormSaveControls } from '../../save-status-indicator'
import { applyConfigUpdateError } from '../apply-config-update-error'
import { IntegrationsEnabledSwitch } from '../../integrations-enabled-switch'
import { useIntegrationsAccess } from '../../../store/integrations-access'

export function isFubSyncContactsSettings(obj: unknown): obj is IntegrationConfigSettingsFollowUpBossSyncContacts {
  return typeof obj === 'object' && obj !== null && 'syncDirection' in obj && 'groupBy' in obj
}
const validationSchema = yup.object({
  enabled: yup.boolean().default(false),
  integrationId: yup.string().required(),
  settings: yup.object({
    syncDirection: yup.string().oneOf(getEnumValues(SyncDirectionEnum)),
    groupBy: yup.string().oneOf(getEnumValues(GroupByEnum)),
  }),
  serviceAccountId: yup.string().required('Follow Up Boss Account Required').uuid(),
})

export interface ConfigureFubSyncContactsProps {
  integrationConfig: IntegrationConfig
}

export function ConfigureFubSyncContacts({ integrationConfig }: ConfigureFubSyncContactsProps) {
  const dispatch = useAppDispatch()
  const { canEdit } = useIntegrationsAccess()

  const startingSettings = () =>
    isFubSyncContactsSettings(integrationConfig.settings)
      ? integrationConfig.settings
      : { groupBy: GroupByEnum.Tags, syncDirection: SyncDirectionEnum.FUBToSendHub }
  const [initialValues, setInitialValues] = useState({ ...integrationConfig, settings: startingSettings() })

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values, { setSubmitting, setStatus, setFieldError, setFieldTouched, resetForm }) => {
      setStatus(null)
      try {
        await dispatch(updateIntegrationConfig({ id: values.id, integrationConfig: values })).unwrap()
        resetForm({ values, status: { type: 'success' } })
      } catch (error) {
        applyConfigUpdateError(error, { setStatus, setFieldError, setFieldTouched })
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    if (!formik.dirty) {
      formik.resetForm({ values: { ...integrationConfig, settings: startingSettings() } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationConfig])

  return (
    <FormikProvider value={formik}>
      <form onSubmit={formik.handleSubmit}>
        <SelectAccount integrationConfig={integrationConfig} />
        <Divider sx={{ marginY: 2 }} />
        <Autocomplete
          sx={{ marginBottom: 2 }}
          disableClearable
          openOnFocus
          disabled={!canEdit}
          id="syncDirection"
          value={formik.values.settings.syncDirection}
          onChange={(event, value) => formik.setFieldValue('settings.syncDirection', value)}
          options={[SyncDirectionEnum.FUBToSendHub, 'SendHub to FUB (Coming soon!)']}
          getOptionDisabled={(option) => option.includes('Coming soon')}
          renderInput={(params) => (
            <TextField
              {...params}
              name="syncDirection"
              label="Sync Direction"
              error={formik.touched.settings?.syncDirection && Boolean(formik.errors.settings?.syncDirection)}
              helperText={formik.touched.settings?.syncDirection && formik.errors.settings?.syncDirection}
            />
          )}
        />
        <Autocomplete
          sx={{ marginBottom: 2 }}
          disableClearable
          openOnFocus
          disabled={!canEdit}
          id="settings.groupBy"
          value={formik.values.settings.groupBy}
          onChange={(event, value) => {
            formik.setFieldValue('settings.groupBy', value)
          }}
          options={getEnumValues(GroupByEnum)}
          renderInput={(params) => (
            <TextField
              {...params}
              name="settings.groupBy"
              label="Group By"
              error={formik.touched.settings?.groupBy && Boolean(formik.errors.settings?.groupBy)}
              helperText={formik.touched.settings?.groupBy && formik.errors.settings?.groupBy}
            />
          )}
        />

        <FormGroup>
          <IntegrationsEnabledSwitch />
        </FormGroup>
        <FormSaveControls />
      </form>
    </FormikProvider>
  )
}
